import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Loader2, Mail, Copy, Building2, Ban, Users } from 'lucide-react';
import { toast } from 'sonner';
import { formatDay } from '../AssignMentorDialog';
import { INVITE_PILL, Spinner, Empty } from './shared';

/** Company and mentor invitations for one programme, plus the all-parents announcement. */
export default function InvitationsList({ program, onChanged }) {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [notifying, setNotifying] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/coordinator/internship-invitations', { params: { program_id: program.id } });
      setInvites(r.data || []);
    } catch { setInvites([]); }
    setLoading(false);
  }, [program.id]);
  useEffect(() => { load(); }, [load]);

  const revoke = async (id) => {
    try {
      await api.put(`/coordinator/internship-invitations/${id}/revoke`);
      toast.success('Invitation revoked');
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not revoke this invitation'); }
  };
  const copy = (link) => { navigator.clipboard.writeText(link); toast.success('Secure link copied'); };
  const canInvite = program.status === 'open';

  if (loading) return <Spinner />;

  return (
    <div data-testid="invitations-list">
      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
        <p className="text-[11px] text-[#6B7280] leading-relaxed max-w-xl">
          Invited companies get a secure link to this programme's opportunity form — they add their own company
          and contact details, and need no account until they are approved.
        </p>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" onClick={() => setInviting(true)} disabled={!canInvite} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-lg text-xs disabled:opacity-40" data-testid="invite-company-btn">
            <Mail className="h-3.5 w-3.5 mr-1" />Send Invites
          </Button>
          <Button size="sm" variant="outline" onClick={() => setNotifying(true)} disabled={!canInvite} className="rounded-lg text-xs border-gray-200 disabled:opacity-40" data-testid="notify-parents-btn">
            <Users className="h-3.5 w-3.5 mr-1" />Notify Parents
          </Button>
        </div>
      </div>
      {!canInvite && <p className="text-[11px] text-[#9CA3AF] mb-3">Open this programme to send invitations.</p>}
      {invites.length === 0 ? (
        <Empty testId="invitations-empty" text="No invitations sent for this programme yet." />
      ) : (
        <div className="space-y-2">
          {invites.map(i => (
            <div key={i.id} className="bg-white rounded-xl border border-[#E5E7EB] px-4 py-3" data-testid={`invitation-row-${i.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-[#1F2937] flex items-center gap-1.5">
                      <Building2 className="h-3.5 w-3.5 text-[#9CA3AF]" />{i.company_name || i.contact_email}
                    </p>
                    <Badge className={`border-0 text-[10px] ${INVITE_PILL[i.status]}`} data-testid={`invite-status-${i.id}`}>{i.status}</Badge>
                  </div>
                  <p className="text-sm text-[#6B7280] mt-0.5">
                    {[i.contact_name, i.company_name ? i.contact_email : null].filter(Boolean).join(' · ')
                      || 'Company details come with their submission'}
                  </p>
                  <p className="text-[11px] text-[#4B5563] mt-1">Invited {formatDay((i.invited_at || '').slice(0, 10))}</p>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="rounded-lg text-xs" onClick={() => copy(i.invite_link)} data-testid={`copy-invite-${i.id}`}>
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                  {i.status !== 'submitted' && i.status !== 'revoked' && (
                    <Button size="sm" variant="ghost" className="rounded-lg text-xs text-red-500 hover:bg-red-50" onClick={() => revoke(i.id)} data-testid={`revoke-invite-${i.id}`}>
                      <Ban className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {inviting && (
        <InviteDialog program={program} onClose={() => setInviting(false)}
          onSent={() => { setInviting(false); load(); onChanged?.(); }} />
      )}
      {notifying && (
        <NotifyParentsDialog program={program} onClose={() => setNotifying(false)} />
      )}
    </div>
  );
}

function InviteDialog({ program, onClose, onSent }) {
  const [emails, setEmails] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const count = emails.split(/[,;\s]+/).map(e => e.trim()).filter(Boolean).length;

  const send = async () => {
    setSaving(true);
    try {
      const r = await api.post('/coordinator/internship-invitations', {
        program_id: program.id, contact_email: emails, note: note.trim() || null,
      });
      const made = r.data.created?.length || 0;
      const skipped = r.data.skipped || [];
      toast.success(`${made} invitation${made === 1 ? '' : 's'} sent`
        + (skipped.length ? ` · ${skipped.length} already invited` : ''));
      onSent();
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not send these invitations'); }
    setSaving(false);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Send Invites</DialogTitle>
          <DialogDescription>Each recipient gets their own secure link to {program.name}'s opportunity form.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Email addresses *</Label>
            <Textarea value={emails} onChange={e => setEmails(e.target.value)}
              placeholder="hr@company.com; careers@firm.in, mentor@example.com"
              className="mt-1.5 rounded-xl border-gray-200 min-h-[80px] resize-none" data-testid="invite-email" />
            <p className="text-[11px] text-[#9CA3AF] mt-1.5">
              One or many — separate addresses with a comma or a semicolon. Each person receives their own
              invitation and link, and fills in their company and contact details on the form.
            </p>
          </div>
          <div>
            <Label className="text-sm font-medium text-[#1F2937]">Message (optional)</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="A short line about what you are looking for" className="mt-1.5 rounded-xl border-gray-200 min-h-[60px] resize-none" data-testid="invite-note" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={send} disabled={saving || !count} className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40" data-testid="send-invite-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            Send {count > 1 ? `${count} invitations` : 'invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function NotifyParentsDialog({ program, onClose }) {
  const [status, setStatus] = useState(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [resend, setResend] = useState(false);

  useEffect(() => {
    api.get(`/coordinator/internship-programs/${program.id}/notify-parents`)
      .then(r => setStatus(r.data)).catch(() => setStatus({ recipient_count: 0, history: [] }));
  }, [program.id]);

  const send = async () => {
    setSending(true);
    try {
      const r = await api.post(`/coordinator/internship-programs/${program.id}/notify-parents`,
        { note: note.trim() || null, resend: resend || !!already });
      toast.success(`Programme details emailed to ${r.data.sent} parent${r.data.sent === 1 ? '' : 's'}`);
      onClose();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Could not notify parents';
      toast.error(detail);
      if (err.response?.status === 409) setResend(true);
    }
    setSending(false);
  };

  const already = status?.history?.length ? status.history[status.history.length - 1] : null;


  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-md" data-testid="notify-parents-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">Notify Parents</DialogTitle>
          <DialogDescription>
            Every activated parent account in your school hears about {program.name}, whatever grade their child is in.
          </DialogDescription>
        </DialogHeader>
        {!status ? <Spinner /> : (
          <div className="space-y-4">
            <div className="rounded-xl bg-[#FFF8F6] border border-[#FFE3DC] px-4 py-3">
              <p className="text-sm font-semibold text-[#1F2937]" data-testid="notify-recipient-count">
                {status.recipient_count} parent account{status.recipient_count === 1 ? '' : 's'} will be emailed
              </p>
              <p className="text-[11px] text-[#6B7280] mt-1 leading-relaxed">
                They receive the programme dates, eligible grades, the application deadline and a link to the
                programme — one email each. Families can explore it and propose a company even before any offer
                is approved.
              </p>
            </div>
            {already && (
              <p className="text-[11px] text-amber-700" data-testid="notify-last-sent">
                Last sent {formatDay((already.at || '').slice(0, 10))} by {already.by || 'the school'} to{' '}
                {already.recipient_count} parent{already.recipient_count === 1 ? '' : 's'}.
                {' '}Sending again notifies everyone a second time.
              </p>
            )}
            <div>
              <Label className="text-sm font-medium text-[#1F2937]">Message (optional)</Label>
              <Textarea value={note} onChange={e => setNote(e.target.value)} placeholder="A line about what the school is hoping for" className="mt-1.5 rounded-xl border-gray-200 min-h-[60px] resize-none" data-testid="notify-note" />
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl border-gray-200">Cancel</Button>
          <Button onClick={send} disabled={sending || !status?.recipient_count}
            className="bg-[#F97D6A] text-white hover:bg-[#F06651] rounded-xl font-semibold disabled:opacity-40"
            data-testid="confirm-notify-btn">
            {sending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {already || resend ? 'Send again' : `Notify ${status?.recipient_count || 0} parents`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
